import { useReassign } from "@synthesis/shared-services/internal/approval/hooks";
import { useTransition } from "@synthesis/shared-services/internal/application/hooks";
import { Button, Modal, SelectNew, selectItemDisplayLabel, setPromiseSnack } from "@synthesis/shared-ui";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";

import { invalidateInternalApplicationQueries } from "../../../shared/utils/invalidate-application-queries";
import { usePageStore } from "../application-detail-page.lib";
import { createEmployeeServerSearch, EMPLOYEE_ACTION_ROLES, EMPLOYEE_ACTION_SNACK_MESSAGES } from "./application-control-employee-modal.lib";
import type { AssignEmployeePopupAction } from "./application-control-popup.lib";

const RAILWAY_ID_MAP: Record<string, string> = {
  VOSTOCHNO_SIBIRSKAYA: "be5dcb49-2c15-4623-8d70-e331eec99f18",
  GORKOVSKAYA: "0178aff2-d734-4cac-8d81-bd3a57cea913",
  DALNEVOSTOCHNAYA: "03313c9d-ba4c-4adb-8310-a164f02f77fc",
  ZABAYKALSKAYA: "7b2c1e05-70f3-4779-8c85-91a48a104283",
  ZAPADNO_SIBIRSKAYA: "ee5d3ac9-c29b-4788-acda-31d05e40a97c",
  KALININGRADSKAYA: "45c34eec-e962-4ca0-afaa-5f4592e0ce76",
  KRASNOYARSKAYA: "d16281e5-5bce-4543-b585-84468d77c15b",
  KUYBYSHEVSKAYA: "3a9188d8-8791-4b3f-bc83-94592e36989d",
  MOSKOVSKAYA: "71d3a446-dc6f-4271-921c-0a9e13785b6a",
  OKTYABRSKAYA: "808d893c-6b3e-47db-917d-47aec9132b50",
  PRIVOLZHSKAYA: "ad85f8d0-b20e-4431-8d84-5a5debfda7b8",
  SVERDLOVSKAYA: "7038a862-5837-40ac-9099-10742d942b10",
  SEVERNAYA: "c5000c51-a2e7-46cc-a80a-270725774e13",
  SEVERO_KAVKAZSKAYA: "86999308-a171-4323-88a6-8f818de7ff12",
  YUGO_VOSTOCHNAYA: "44b41ff2-60e9-48f8-af9b-70ac1ecad47a",
  YUZHNO_URALSKAYA: "9ff7d55e-edf6-41ce-8e02-e1affbcf75b5",
};

const EmployeeSelectModal = ({ popupAction }: { popupAction: AssignEmployeePopupAction }) => {
  const store = usePageStore();
  const queryClient = useQueryClient();
  const transitionMutation = useTransition();
  const reassignMutation = useReassign();
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedEmployeeLabel, setSelectedEmployeeLabel] = useState<string>();

  const selectName = `assign-employee-${popupAction.action}`;
  const railwayKey = store?.application?.railway;
  const roadsIds = useMemo(() => {
    if (!railwayKey) {
      return [];
    }
    const id = RAILWAY_ID_MAP[railwayKey];
    return id ? [id] : [];
  }, [railwayKey]);
  const employeeServerSearch = useMemo(
    () => createEmployeeServerSearch(EMPLOYEE_ACTION_ROLES[popupAction.action], roadsIds),
    [popupAction.action, roadsIds],
  );

  const isPending = transitionMutation.isPending || reassignMutation.isPending;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      store?.setPopupAction(null);
    }
  };

  const handleSubmit = () => {
    if (!selectedUserId) {
      return;
    }

    const promise = (async () => {
      const { target } = popupAction;

      if (target.kind === "transition") {
        await transitionMutation.mutateAsync({
          path: { requestId: popupAction.requestId, action: target.transitionAction },
          body: { assigneeId: selectedUserId },
        });
      } else {
        await reassignMutation.mutateAsync({
          path: { requestId: popupAction.requestId, slot: target.slot },
          body: { assigneeId: selectedUserId },
        });
      }

      await invalidateInternalApplicationQueries(queryClient, popupAction.requestId);
      store?.setPopupAction(null);
    })();

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: EMPLOYEE_ACTION_SNACK_MESSAGES[popupAction.action],
      },
    });
  };

  return (
    <Modal.Root isOpen setIsOpen={handleOpenChange}>
      <Modal.Content className="flex max-h-[calc(100vh-4rem)] w-[min(700px,calc(100vw-2rem))] flex-col overflow-hidden">
        <Modal.Title>Назначение ответственного</Modal.Title>
        <div className="min-h-0 flex-1 overflow-y-auto p-8">
          <h3 className="text-type-common heading-h4 after:text-type-error mb-3 whitespace-nowrap after:content-['*']">Назначьте ответственного</h3>
          <SelectNew
            name={selectName}
            label="ФИО"
            required
            placeholder="Выберите из списка"
            value={selectedUserId}
            selectedItemLabel={selectedEmployeeLabel}
            onChange={(value, item) => {
              setSelectedUserId(value);
              setSelectedEmployeeLabel(item ? selectItemDisplayLabel(item) : undefined);
            }}
            disabled={isPending}
            serverSearch={employeeServerSearch}
            size="md"
            visibleItemsCount={6}
            // minVisibleItemsCount={5}
            onClear={() => {
              setSelectedUserId("");
              setSelectedEmployeeLabel(undefined);
            }}
            contentProps={{ style: { zIndex: 50 } }}
          />
        </div>
        <div className="flex shrink-0 gap-4 px-8 pb-6 pt-2">
          <Button type="button" theme="primary" size="lg" disabled={isPending || !selectedUserId} onClick={handleSubmit}>
            Направить
          </Button>
          <Button type="button" theme="secondary" size="lg" disabled={isPending} onClick={() => handleOpenChange(false)}>
            Отмена
          </Button>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};

export const ApplicationControlEmployeeModal = observer(() => {
  const store = usePageStore();
  const popupAction = store?.popupAction;

  if (!popupAction || popupAction.type !== "assign-employee") {
    return null;
  }

  return <EmployeeSelectModal popupAction={popupAction} />;
});
